/**
 * Global "active jobs" indicator, mounted in the AppHeader right cluster so it is
 * visible from every screen. Polls GET /api/jobs?status=running (already scoped to
 * the current user by er_login on the backend) every ~4s.
 *
 * - 0 running  -> the trigger is hidden entirely (nothing in the bar).
 * - ≥1 running -> a compact spinner + count badge; clicking opens a dropdown with one
 *   row per active job: a human label by kind, an accent progress bar (progress/total)
 *   and a "progress / total (P%)" mono caption.
 *
 * Edge case (real for sample_n fills): while total===0 the job is still resolving its
 * selection, so we render an indeterminate "Подготовка…" bar instead of a bogus 0/0 0%.
 *
 * Progress-bar percent logic mirrors <JobProgress/>; the accent fill uses the
 * raw var(--accent-hex) token directly (not the shadcn HSL var(--accent)).
 */

import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Loader2, AlertTriangle } from "lucide-react";

import { type JobListItem } from "@/api/client";
import { useActiveJobs } from "@/api/hooks";
import { TouchLink } from "@/components/jobs/TouchLink";
import { cn } from "@/lib/utils";

/** Human-readable label per job kind. Falls back to the raw kind for unknown ones. */
const KIND_LABELS: Record<string, string> = {
  touch_fill: "Наполнение касания",
  bulk_delete: "Удаление активистов",
  readiness_fill: "Готовность из файла",
  bulk_edit_file: "Обновление из файла",
  uik_branch_fill: "УИК → отделение из файла",
  lottery_turnout: "ЕДГ-лотерея: явка",
  export: "Экспорт",
};

function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

/**
 * Job label for a popover row. touch_fill (touch_id present) → clickable <TouchLink/>
 * with the touch name and an optional « · результат «…»» suffix; other kinds fall back
 * to the description / kind label as before. The Link navigates even from inside the
 * Radix popover (a click on it closes the menu and routes — both are fine).
 */
function JobLabel({ job }: { job: JobListItem }) {
  if (job.touch_id != null) {
    const result = job.result?.trim();
    return (
      <>
        {"Наполнение касания "}
        <TouchLink touchId={job.touch_id} />
        {result ? ` · результат «${result}»` : ""}
      </>
    );
  }
  const d = job.description?.trim();
  return <>{d && d.length > 0 ? d : kindLabel(job.kind)}</>;
}

/** Accent fill: raw accent token (theme-aware), not the shadcn HSL var(--accent). */
const ACCENT_HEX = "var(--accent-hex)";

function ActiveJobRow({ job }: { job: JobListItem }) {
  const total = job.total || 0;
  const progress = job.progress || 0;
  // total===0 -> the job hasn't resolved its selection yet (e.g. sample_n): show an
  // indeterminate "preparing" bar, never "0 / 0 0%".
  const preparing = total <= 0;
  const pct = preparing ? 0 : Math.min(100, Math.round((progress / total) * 100));

  return (
    <div className="px-3 py-2.5" data-testid="active-job-row">
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-[13px] font-medium text-foreground">
          <JobLabel job={job} />
        </span>
        <span className="shrink-0 font-mono text-[11px] tabular-nums text-[var(--text-muted)]">
          {preparing
            ? "Подготовка…"
            : `${progress.toLocaleString("ru-RU")} / ${total.toLocaleString("ru-RU")} (${pct}%)`}
        </span>
      </div>

      <div className="mt-1.5 h-[6px] w-full overflow-hidden rounded-full bg-[var(--border-soft)]">
        {preparing ? (
          // Indeterminate pulsing sliver while the selection resolves.
          <div
            className="h-full w-1/3 animate-pulse rounded-full"
            style={{ backgroundColor: ACCENT_HEX, opacity: 0.6 }}
          />
        ) : (
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: ACCENT_HEX }}
          />
        )}
      </div>

      {job.failed_count > 0 && (
        <div className="mt-1 flex items-center gap-1 text-[11px] text-[var(--danger)]">
          <AlertTriangle className="h-3 w-3" />
          <span>Ошибок: {job.failed_count.toLocaleString("ru-RU")}</span>
        </div>
      )}
    </div>
  );
}

export function ActiveJobsIndicator() {
  // Poll running jobs every ~4s on a fixed interval (see useActiveJobs) so the count keeps
  // updating even when the list is momentarily empty — a new job may start at any time while
  // the user works elsewhere.
  const query = useActiveJobs(4000);
  const jobs = React.useMemo(() => query.data ?? [], [query.data]);
  const count = jobs.length;

  // Nothing active -> render nothing at all (keeps the bar clean).
  if (count === 0) return null;

  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={`Активные задачи: ${count}`}
          className="relative flex h-[28px] items-center gap-1.5 rounded-[8px] border bg-[var(--app-bg)] px-2 text-[var(--accent-weak-text)] transition-colors hover:bg-[var(--accent-weak)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-hex)]"
          style={{ borderColor: "var(--border-hex)" }}
          data-testid="active-jobs-trigger"
        >
          <Loader2 className="h-[14px] w-[14px] animate-spin" aria-hidden />
          <span
            className="min-w-[16px] rounded-full px-1 text-center text-[11px] font-semibold tabular-nums text-white"
            style={{ backgroundColor: ACCENT_HEX }}
          >
            {count}
          </span>
        </button>
      </DropdownMenuPrimitive.Trigger>

      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="end"
          sideOffset={6}
          className={cn(
            "z-50 w-[320px] overflow-hidden rounded-[10px] border bg-[var(--panel)] text-card-foreground shadow-md",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
          style={{ borderColor: "var(--border-hex)" }}
          data-testid="active-jobs-popover"
        >
          <div className="border-b px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-muted)]" style={{ borderColor: "var(--border-soft)" }}>
            Активные задачи · {count}
          </div>
          <div className="max-h-[320px] divide-y divide-[var(--border-soft)] overflow-y-auto">
            {jobs.map((job) => (
              <ActiveJobRow key={job.id} job={job} />
            ))}
          </div>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}
